/**
 * FERP Portal - Regulatory Node List Page (DRR-125 Sprint 4)
 *
 * Config Type #8 (rn-): Regulatory nodes — external authorities, bodies,
 * and enforcement agencies that mandate workflows, rulesets, and compliance
 * requirements. Used as MANDATED_BY edge targets in the care graph (DRR-110).
 *
 * NOTE: No GraphQL list query exists for rn- config records yet. RegNodes
 * are referenced via regnodeRef on RuleSet configs, not via a dedicated
 * list resolver. This page shows the known seeded regulatory nodes from
 * the regulatory-nodes/ directory.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: displays what backend returns
 * - Admin view layer only
 */

import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  Table,
  Paper,
} from '@mantine/core';
import {
  IconBuildingBank,
  IconInfoCircle,
  IconMapPin,
} from '@tabler/icons-react';

// Known regulatory nodes seeded from regulatory-nodes/ directory.
// A GraphQL list resolver for rn- configs is not yet implemented.
const KNOWN_REG_NODES = [
  // State — MN
  {
    nodeId: 'rn-1-0-001',
    name: 'Minnesota Department of Health — Assisted Living Licensing',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'licensing',
    effectiveDate: '2021-08-01',
    enforcementBody: 'Minnesota Department of Health',
    industry: 'assisted_living',
  },
  {
    nodeId: 'rn-1-0-002',
    name: 'Minnesota Department of Health — Adult Foster Care',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'licensing',
    effectiveDate: '2021-08-01',
    enforcementBody: 'Minnesota Department of Health',
    industry: 'adult_foster_care',
  },
  {
    nodeId: 'rn-1-0-003',
    name: 'Minnesota Department of Human Services — HCBS Waiver',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'billing',
    effectiveDate: '2021-01-01',
    enforcementBody: 'Minnesota Department of Human Services',
    industry: 'hcbs_waiver',
  },
  {
    nodeId: 'rn-1-1-001',
    name: 'Minnesota Department of Health — Environmental Health Division',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'safety',
    effectiveDate: '1967-01-01',
    enforcementBody: 'Minnesota Department of Health',
    industry: 'healthcare',
  },
  {
    nodeId: 'rn-1-1-002',
    name: 'Minnesota Radiation Control Program (MDH)',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'safety',
    effectiveDate: '1967-01-01',
    enforcementBody: 'Minnesota Department of Health — Radiation Control',
    industry: 'dental',
  },
  {
    nodeId: 'rn-1-2-001',
    name: 'Minnesota Board of Nursing',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'credentialing',
    effectiveDate: '1951-01-01',
    enforcementBody: 'Minnesota Board of Nursing',
    industry: 'nursing',
  },
  {
    nodeId: 'rn-1-2-002',
    name: 'Minnesota Board of Pharmacy',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'credentialing',
    effectiveDate: '1951-01-01',
    enforcementBody: 'Minnesota Board of Pharmacy',
    industry: 'pharmacy',
  },
  {
    nodeId: 'rn-1-2-003',
    name: 'Minnesota Health Care Workforce Intelligence System',
    jurisdiction: 'state',
    state: 'MN',
    authorityType: 'workforce',
    effectiveDate: '2021-08-01',
    enforcementBody: 'Minnesota Department of Health — Office of Rural Health',
    industry: 'healthcare',
  },
  // Payer — MN
  {
    nodeId: 'rn-1-3-001',
    name: 'Minnesota Medical Assistance (Medicaid)',
    jurisdiction: 'payer',
    state: 'MN',
    authorityType: 'billing',
    effectiveDate: '1966-01-01',
    enforcementBody: 'Minnesota Department of Human Services',
    industry: 'medicaid',
  },
  {
    nodeId: 'rn-1-3-002',
    name: 'UCare Minnesota',
    jurisdiction: 'payer',
    state: 'MN',
    authorityType: 'billing',
    effectiveDate: '1984-01-01',
    enforcementBody: 'UCare Minnesota',
    industry: 'managed_care',
  },
  // Federal
  {
    nodeId: 'rn-0-1-002',
    name: 'Centers for Medicare & Medicaid Services (CMS)',
    jurisdiction: 'federal',
    state: null,
    authorityType: 'billing',
    effectiveDate: '1965-07-30',
    enforcementBody: 'U.S. Department of Health and Human Services — CMS',
    industry: 'medicare_medicaid',
  },
];

const JURISDICTION_COLORS: Record<string, string> = {
  federal: 'red',
  state: 'blue',
  payer: 'green',
};

const AUTHORITY_COLORS: Record<string, string> = {
  licensing: 'violet',
  billing: 'teal',
  safety: 'orange',
  credentialing: 'cyan',
  workforce: 'grape',
};

export function RegNodeListPage() {
  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Regulatory Nodes</Title>
            <Text c="dimmed" size="sm">
              Config type rn- — external regulatory authorities and enforcement bodies
            </Text>
          </div>
          <Badge variant="light" color="blue" size="lg">
            {KNOWN_REG_NODES.length} Nodes
          </Badge>
        </Group>

        {/* Info banner */}
        <Alert
          icon={<IconInfoCircle size={16} />}
          color="blue"
          title="No Live GraphQL Query"
        >
          Regulatory node configs are seeded from the{' '}
          <Text span size="sm" ff="monospace">regulatory-nodes/</Text>{' '}
          directory and referenced via{' '}
          <Text span size="sm" ff="monospace">regnodeRef</Text>{' '}
          on RuleSet configs. A dedicated list resolver for rn- records is not yet implemented.
          The table below reflects the currently seeded regulatory nodes.
        </Alert>

        {/* Architecture card */}
        <Card withBorder p="md">
          <Group gap="sm" mb="xs">
            <ThemeIcon variant="light" color="blue">
              <IconBuildingBank size={18} />
            </ThemeIcon>
            <Text fw={600}>How RegNodes Are Used</Text>
          </Group>
          <Text size="sm" c="dimmed">
            RegNodes are the targets of{' '}
            <Text span size="sm" ff="monospace">MANDATED_BY</Text>{' '}
            edges in the care graph (DRR-110). A task group or ruleset with a{' '}
            <Text span size="sm" ff="monospace">MANDATED_BY</Text>{' '}
            edge points to an rn- config ID, declaring which regulatory body requires it.
            This populates the{' '}
            <Text span size="sm" ff="monospace">mandate_ref</Text>{' '}
            field on tasks (DRR-117).
          </Text>
        </Card>

        {/* Table */}
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Config ID</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Jurisdiction</Table.Th>
                <Table.Th>Authority Type</Table.Th>
                <Table.Th>Location</Table.Th>
                <Table.Th>Effective</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {KNOWN_REG_NODES.map((rn) => (
                <Table.Tr key={rn.nodeId}>
                  <Table.Td>
                    <Text size="sm" fw={500} ff="monospace">{rn.nodeId}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={2}>{rn.name}</Text>
                    <Text size="xs" c="dimmed">{rn.enforcementBody}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={JURISDICTION_COLORS[rn.jurisdiction] ?? 'gray'}
                      size="sm"
                    >
                      {rn.jurisdiction}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant="outline"
                      color={AUTHORITY_COLORS[rn.authorityType] ?? 'gray'}
                      size="sm"
                    >
                      {rn.authorityType}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {rn.state && (
                        <Group gap={4}>
                          <IconMapPin size={12} />
                          <Text size="sm">{rn.state}</Text>
                        </Group>
                      )}
                      <Text size="xs" c="dimmed">{rn.industry}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{rn.effectiveDate}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>

        <Text size="xs" c="dimmed">
          To add regulatory nodes, create new regulatory node configurations
          following the scope-encoded naming convention (DRR-089) and re-seed.
        </Text>
      </Stack>
    </Container>
  );
}

export default RegNodeListPage;
